import { useTranslation } from 'react-i18next';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useLazyGetSystemRecoveryKeyQuery } from '@/redux/slices/mdmManagementApiSlice';
import { DeviceAsset } from '@/types/assets';
import {
  ModalV2,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalAction,
  Text,
  Flex,
  TextInput,
  Skeleton,
  spaces,
  sizes,
} from '@electricjs/arc';
import { useEffect, useState } from 'react';
import CopyButton from '../../../../../common/CopyButton';

type RecoveryKeyModalProps = {
  visible: boolean;
  hide: () => void;
  deviceAsset: DeviceAsset;
  isLoading?: boolean;
};

const RecoveryKeyModal = ({
  deviceAsset,
  visible,
  hide,
}: RecoveryKeyModalProps) => {
  const { t } = useTranslation('assets');
  const { showErrorToast } = useGlobalUI();
  const organizationId = useGetOrganizationId();
  const [recoveryKey, setRecoveryKey] = useState('');

  const [getRecoveryKey, { isFetching: isRecoveryKeyLoading }] =
    useLazyGetSystemRecoveryKeyQuery();

  useEffect(() => {
    const fetchRecoveryKey = async () => {
      if (
        !organizationId ||
        !deviceAsset.device?.id ||
        !deviceAsset.serialNumber
      )
        return;
      getRecoveryKey({
        organizationId,
        serialNumber: deviceAsset.serialNumber,
      })
        .unwrap()
        .then(result => {
          setRecoveryKey(result.recoveryKey);
        })
        .catch(error => {
          console.error('Failed to fetch recovery key:', error);
          showErrorToast({
            id: 'recovery-key-fetch-error-toast',
            title: t('deviceSecurityPanel.recoveryKeyNotAvailable'),
            message: t('deviceSecurityPanel.recoveryKeyErrorMessage'),
          });
        });
    };
    fetchRecoveryKey();
  }, [
    organizationId,
    deviceAsset.device?.id,
    deviceAsset.serialNumber,
    getRecoveryKey,
    showErrorToast,
    t,
  ]);

  return (
    <ModalV2 visible={visible} hide={hide} ariaLabelledby="recovery-key-modal">
      <ModalHeader>
        <Text variant="heading-2">
          {t('deviceSecurityPanel.recoveryKeyModal.title')}
        </Text>
      </ModalHeader>
      <ModalBody>
        <Flex
          flexDirection="column"
          gap={spaces.space_l_rem}
          mb={spaces.space_xs_rem}>
          <Text variant="body">
            {t('deviceSecurityPanel.recoveryKeyModal.description')}
          </Text>
          <Flex flexDirection="column" gap={spaces.space_s_rem}>
            <Text variant="label-small">
              {t('deviceSecurityPanel.recoveryKeyModal.label')}
            </Text>
            {isRecoveryKeyLoading ? (
              <Skeleton height={sizes.size_48} width="50rem" />
            ) : (
              <Flex alignItems="center" gap={spaces.space_s_rem}>
                <TextInput
                  id="recovery-key-input"
                  value={recoveryKey}
                  readOnly
                  minWidth="40rem"
                  style={{
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    height: sizes.size_48,
                    borderRadius: sizes.size_8,
                  }}
                />
                <CopyButton
                  id="copy-recovery-key-button"
                  textToCopy={recoveryKey}>
                  {t('deviceSecurityPanel.recoveryKeyModal.copy')}
                </CopyButton>
              </Flex>
            )}
          </Flex>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction id="close-recovery-key-modal" onClick={hide}>
            {t('deviceSecurityPanel.recoveryKeyModal.close')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default RecoveryKeyModal;
